import { useCallback, useLayoutEffect, type MouseEvent } from 'react';
import { useLocation, useNavigate, useNavigationType } from 'react-router-dom';
import { takeRememberedPublicationSource } from '@/app/publication-navigation';

const CUSTOM_SCROLL_ROUTES = ['/coach', '/workouts'];

export function shouldResetRouteScroll(pathname: string, navigationType: string): boolean {
  if (navigationType === 'POP') return false;
  return !CUSTOM_SCROLL_ROUTES.some((route) => pathname === route || pathname.startsWith(`${route}/`));
}

export function RouteScrollManager() {
  const { pathname, search } = useLocation();
  const navigationType = useNavigationType();

  useLayoutEffect(() => {
    if (shouldResetRouteScroll(pathname, navigationType)) window.scrollTo(0, 0);
  }, [navigationType, pathname, search]);

  return null;
}

export function useCatalogScroll(ready: boolean) {
  const { pathname, search } = useLocation();
  const key = `catalog-scroll:${pathname}${search}`;

  useLayoutEffect(() => {
    if (!ready) return;

    const stored = Number(sessionStorage.getItem(key));
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo(0, Number.isFinite(stored) ? stored : 0);
    });

    return () => {
      window.cancelAnimationFrame(frame);
      sessionStorage.setItem(key, String(window.scrollY));
    };
  }, [key, ready]);
}

export function useDetailScrollTop(key: string) {
  const navigationType = useNavigationType();
  const { pathname, search } = useLocation();
  useLayoutEffect(() => {
    const remembered = navigationType === 'POP'
      ? takeRememberedPublicationSource(`${pathname}${search}`)
      : undefined;
    if (!remembered) {
      window.scrollTo(0, 0);
      return;
    }
    const frame = window.requestAnimationFrame(() => {
      window.scrollTo(0, remembered.scrollY);
      if (remembered.focusId) document.getElementById(remembered.focusId)?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [key, navigationType, pathname, search]);
}

export function useRouteBack(hasReturnState: boolean) {
  const navigate = useNavigate();
  return useCallback((event: MouseEvent<HTMLAnchorElement>) => {
    if (
      !hasReturnState
      || event.button !== 0
      || event.metaKey
      || event.ctrlKey
      || event.shiftKey
      || event.altKey
    ) return;

    event.preventDefault();
    navigate(-1);
  }, [hasReturnState, navigate]);
}
